import { getCustomerApis } from "../../../../api/Customer";
import React, { useEffect, useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const CustomerList = ({
  searchKeyword = "",
  sortBy = "",
  sortOrder = "asc",
  currentPage = 1,
  onPageChange,
}) => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customersPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  // Filter and sort data whenever filters change
  useEffect(() => {
    let filtered = [...customers];

    // Apply search filter
    if (searchKeyword.trim()) {
      filtered = filtered.filter(
        (customer) =>
          customer.fullName
            .toLowerCase()
            .includes(searchKeyword.toLowerCase()) ||
          customer.email.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          (customer.phoneNumber &&
            customer.phoneNumber
              .toLowerCase()
              .includes(searchKeyword.toLowerCase()))
      );
    }

    // Apply sorting
    if (sortBy) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        if (sortBy === "fullName") {
          aValue = a.fullName.toLowerCase();
          bValue = b.fullName.toLowerCase();
        } else if (sortBy === "email") {
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
        } else if (sortBy === "phoneNumber") {
          aValue = (a.phoneNumber || "").toLowerCase();
          bValue = (b.phoneNumber || "").toLowerCase();
        }

        if (sortOrder === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    setFilteredCustomers(filtered);
  }, [customers, searchKeyword, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      const response = await getCustomerApis();
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  // Lấy chỉ số khách hàng đầu và cuối của trang hiện tại
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstCustomer,
    indexOfLastCustomer
  );

  // Chuyển đổi trang
  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  return (
    <div>
      {/* Results Info */}
      {filteredCustomers.length > 0 && (
        <div className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
          Hiển thị {indexOfFirstCustomer + 1} -{" "}
          {Math.min(indexOfLastCustomer, filteredCustomers.length)} trong tổng
          số {filteredCustomers.length} khách hàng
          {searchKeyword && (
            <span className="ml-2 text-blue-600">
              (tìm kiếm: "{searchKeyword}")
            </span>
          )}
        </div>
      )}

      {/* Table Section */}
      {currentCustomers.length > 0 ? (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  STT
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Họ Tên
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SĐT
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Loại Tài Khoản
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentCustomers.map((customer, index) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {indexOfFirstCustomer + index + 1}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    <div
                      className="truncate font-medium"
                      title={customer.fullName}
                    >
                      {customer.fullName}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    <div className="truncate" title={customer.email}>
                      {customer.email}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {customer.phoneNumber || "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.isAdmin
                          ? "bg-purple-100 text-purple-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {customer.isAdmin ? "Admin" : "Khách Hàng"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchKeyword
              ? "Không tìm thấy khách hàng"
              : "Không có khách hàng nào"}
          </h3>
          <p className="text-gray-500">
            {searchKeyword
              ? `Không có khách hàng nào phù hợp với từ khóa "${searchKeyword}"`
              : "Chưa có khách hàng nào trong hệ thống"}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-700">
            Trang {currentPage} / {totalPages}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            {currentPage > 1 && (
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <LeftOutlined className="mr-1" />
                Trước
              </button>
            )}

            {/* Page Numbers */}
            <div className="flex space-x-1">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    currentPage === index + 1
                      ? "bg-blue-600 text-white border border-blue-600"
                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {/* Next Button */}
            {currentPage < totalPages && (
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                Sau
                <RightOutlined className="ml-1" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
